import React from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { WEEKDAY_LABELS, weeklyToDayState, dayStateToWeekly } from "../../utils/psychSchedule";

/* ── Styled HH : MM selector ──────────────────────────────────────── */
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 06 – 22
const MINUTES = [0, 15, 30, 45];

const selCls =
  "rounded-lg border border-blue-gray-200 bg-white py-2 text-[0.9375rem] text-brand-dark focus:border-brand-lavender focus:outline-none focus:ring-1 focus:ring-brand-lavender/40 disabled:opacity-50 cursor-pointer appearance-none text-center";

function TimeSelect({ value, onChange, disabled }) {
  const parts = (value || "09:00").split(":");
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;

  function setH(newH) {
    onChange(`${String(newH).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  function setM(newM) {
    onChange(`${String(h).padStart(2, "0")}:${String(newM).padStart(2, "0")}`);
  }

  return (
    <div className="inline-flex items-center rounded-xl border border-blue-gray-200 bg-white overflow-hidden focus-within:border-brand-lavender focus-within:ring-1 focus-within:ring-brand-lavender/40">
      {/* Hour */}
      <select
        value={h}
        disabled={disabled}
        onChange={(e) => setH(Number(e.target.value))}
        className={`${selCls} border-0 ring-0 focus:ring-0 pl-3 pr-1 w-[3.25rem]`}
        aria-label="Hora"
      >
        {HOURS.map((hh) => (
          <option key={hh} value={hh}>
            {String(hh).padStart(2, "0")}
          </option>
        ))}
      </select>

      <span className="text-brand-gray font-semibold select-none px-0.5">:</span>

      {/* Minute */}
      <select
        value={m}
        disabled={disabled}
        onChange={(e) => setM(Number(e.target.value))}
        className={`${selCls} border-0 ring-0 focus:ring-0 pl-1 pr-3 w-[3.25rem]`}
        aria-label="Minutos"
      >
        {MINUTES.map((mm) => (
          <option key={mm} value={mm}>
            {String(mm).padStart(2, "0")}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ── WeeklyScheduleEditor ─────────────────────────────────────────── */
/**
 * Edición de horario por día (1–7), varias franjas por día.
 * Diseño compacto: una fila por día (toggle + franjas en línea) + atajo Lun–Vie.
 * value / onChange usan formato API: weekly: [{ weekday, ranges: [{start,end}] }]
 */
export default function WeeklyScheduleEditor({ weekly, onChange, disabled = false }) {
  const dayMap = weeklyToDayState(weekly);

  function commit(nextMap) {
    onChange(dayStateToWeekly(nextMap));
  }

  function updateDay(wd, patch) {
    commit({ ...dayMap, [wd]: { ...dayMap[wd], ...patch } });
  }

  function setRange(wd, idx, field, value) {
    const ranges = [...(dayMap[wd].ranges || [])];
    ranges[idx] = { ...ranges[idx], [field]: value };
    updateDay(wd, { ranges });
  }

  function addRange(wd) {
    const ranges = [...(dayMap[wd].ranges || []), { start: "09:00", end: "13:00" }];
    updateDay(wd, { enabled: true, ranges });
  }

  function removeRange(wd, idx) {
    const ranges = (dayMap[wd].ranges || []).filter((_, i) => i !== idx);
    updateDay(wd, { ranges: ranges.length ? ranges : [{ start: "09:00", end: "17:00" }] });
  }

  function copyToWeekdays() {
    const source =
      (dayMap[1].enabled && dayMap[1]) ||
      [1, 2, 3, 4, 5, 6, 7].map((d) => dayMap[d]).find((d) => d.enabled) ||
      dayMap[1];
    const ranges = (source.ranges || [{ start: "09:00", end: "17:00" }]).map((r) => ({ ...r }));
    const next = { ...dayMap };
    for (let wd = 1; wd <= 5; wd += 1) {
      next[wd] = { enabled: true, ranges: ranges.map((r) => ({ ...r })) };
    }
    commit(next);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="portal-label">Horario por día (Ciudad de México)</span>
        {!disabled && (
          <button
            type="button"
            onClick={copyToWeekdays}
            className="portal-meta font-medium text-brand-lavender hover:underline"
          >
            Copiar a Lun–Vie
          </button>
        )}
      </div>

      <div className="divide-y divide-brand-lavender/15 rounded-xl border border-brand-brown/15 bg-white">
        {[1, 2, 3, 4, 5, 6, 7].map((wd) => {
          const day = dayMap[wd];
          return (
            <div
              key={wd}
              className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-start sm:gap-4"
            >
              {/* Day toggle */}
              <label className="flex w-32 shrink-0 items-center gap-2 pt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  disabled={disabled}
                  onChange={(e) => updateDay(wd, { enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-brand-brown/40 text-brand-lavender focus:ring-brand-lavender"
                />
                <span
                  className={`portal-value !text-[0.9375rem] ${
                    day.enabled ? "font-semibold" : "text-brand-gray"
                  }`}
                >
                  {WEEKDAY_LABELS[wd]}
                </span>
              </label>

              {/* Franjas */}
              {day.enabled ? (
                <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                  {(day.ranges || []).map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <TimeSelect
                        value={r.start}
                        disabled={disabled}
                        onChange={(v) => setRange(wd, idx, "start", v)}
                      />
                      <span className="portal-meta">–</span>
                      <TimeSelect
                        value={r.end}
                        disabled={disabled}
                        onChange={(v) => setRange(wd, idx, "end", v)}
                      />
                      {!disabled && (day.ranges || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRange(wd, idx)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                          aria-label="Quitar franja"
                        >
                          <XMarkIcon className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  ))}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => addRange(wd)}
                      className="inline-flex items-center gap-1 portal-meta font-medium text-brand-lavender hover:underline"
                    >
                      <PlusIcon className="h-3.5 w-3.5" aria-hidden />
                      Franja
                    </button>
                  )}
                </div>
              ) : (
                <span className="portal-meta pt-1.5">Cerrado</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
